"use client";

import React from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Spinner,
  useDisclosure,
  HStack,
  List,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { Phone } from "lucide-react";
import Layout from "./Layout.tsx";
import {
  phoneNumberService,
  type PhoneNumber,
} from "../services/phoneNumberService.ts";
import { agentService, type Agent } from "../services/agentService.ts";
import { CreatePhoneNumberDialog } from "./phone-numbers/CreatePhoneNumberDialog.tsx";
import { PhoneNumberDetails } from "./phone-numbers/PhoneNumberDetails.tsx";
import { useAppTheme } from "../ThemeContext.tsx";
import { ImportPhoneNumberDialog } from "./phone-numbers/ImportPhoneNumberDialog.tsx";

const PhoneNumbers = () => {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const [phoneNumbers, setPhoneNumbers] = React.useState<PhoneNumber[]>([]);
  const [selectedNumber, setSelectedNumber] =
    React.useState<PhoneNumber | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const { isOpen, onClose } = useDisclosure();
  const {
    isOpen: isNoAgentsOpen,
    onOpen: onNoAgentsOpen,
    onClose: onNoAgentsClose,
  } = useDisclosure();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false);

  const loadData = React.useCallback(async () => {
    try {
      const [numbers, agentsList] = await Promise.all([
        phoneNumberService.list(),
        agentService.list(),
      ]);
      setPhoneNumbers(numbers);
      setAgents(agentsList);
      if (numbers.length > 0 && !selectedNumber) {
        setSelectedNumber(numbers[0]);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedNumber]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePhoneNumberCreated = () => {
    loadData();
  };

  const handlePhoneNumberUpdated = async () => {
    if (selectedNumber) {
      try {
        const updatedNumber = await phoneNumberService.get(
          selectedNumber.phone_number
        );
        setPhoneNumbers((prev) =>
          prev.map((num) =>
            num.phone_number === updatedNumber.phone_number
              ? updatedNumber
              : num
          )
        );
        setSelectedNumber(updatedNumber);
      } catch (error) {
        console.error("Error refreshing phone number:", error);
      }
    }
  };

  const handlePhoneNumberDeleted = () => {
    setSelectedNumber(null);
    loadData();
  };

  if (isLoading) {
    return (
      <Layout>
        <Flex justify="center" align="center" h="100vh" w="100%">
          <Spinner size="xl" />
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Flex h="100vh">
        {/* Sidebar */}
        <Box
          w="300px"
          bg={isDark ? "gray.900" : "gray.50"}
          borderRight="1px"
          borderColor={isDark ? "gray.700" : "gray.200"}
        >
          <Flex
            p={4}
            justify="space-between"
            align="center"
            borderBottom="1px"
            borderColor={isDark ? "gray.700" : "gray.200"}
          >
            <HStack spacing={3}>
              <Phone size={20} />
              <Heading size="md" color={isDark ? "white" : "gray.800"}>
                Phone Numbers
              </Heading>
            </HStack>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Add phone number"
                icon={<AddIcon />}
                variant="solid"
                colorScheme={isDark ? "gray" : "blackAlpha"}
                size="sm"
              />
              <MenuList
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
              >
                <MenuItem
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M9.25 9.25V4.75H10.75V9.25H15.25V10.75H10.75V15.25H9.25V10.75H4.75V9.25H9.25Z"
                        fill="currentColor"
                      ></path>
                    </svg>
                  }
                  onClick={() => setIsCreateDialogOpen(true)}
                  _hover={{ bg: isDark ? "gray.500" : "gray.200" }}
                  bg={isDark ? "gray.700" : "white"}
                >
                  Buy New Number
                </MenuItem>
                <MenuItem
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M13.0375 10.0371L16.75 13.7496L13.0375 17.4621L11.977 16.4016L13.879 14.4989L4 14.4996V12.9996H13.879L11.977 11.0976L13.0375 10.0371ZM6.9625 2.53711L8.023 3.59761L6.121 5.49961H16V6.99961H6.121L8.023 8.90161L6.9625 9.96211L3.25 6.24961L6.9625 2.53711V2.53711Z"
                        fill="currentColor"
                      ></path>
                    </svg>
                  }
                  onClick={() => {
                    if (agents.length === 0) {
                      onNoAgentsOpen();
                    } else {
                      setIsImportDialogOpen(true);
                    }
                  }}
                  _hover={{ bg: isDark ? "gray.500" : "gray.200" }}
                  bg={isDark ? "gray.700" : "white"}
                >
                  Connect to your number via SIP trunking
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>

          <List spacing={0}>
            {phoneNumbers.map((number) => (
              <ListItem
                key={number.phone_number}
                p={4}
                cursor="pointer"
                bg={
                  selectedNumber?.phone_number === number.phone_number
                    ? isDark
                      ? "gray.700"
                      : "gray.300"
                    : "transparent"
                }
                _hover={{ bg: isDark ? "gray.500" : "gray.200" }}
                onClick={() => setSelectedNumber(number)}
              >
                <HStack spacing={3}>
                  <Phone size={16} />
                  <Text color={isDark ? "white" : "gray.800"}>
                    {number.nickname || number.phone_number}
                  </Text>
                </HStack>
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Main Content */}
        <Box flex="1" bg={isDark ? "gray.800" : "white"}>
          {selectedNumber ? (
            <Box>
              <PhoneNumberDetails
                phoneNumber={selectedNumber}
                agents={agents.map((agent) => ({
                  id: agent.agent_id,
                  name: agent.agent_name || agent.agent_id,
                }))}
                onUpdate={handlePhoneNumberUpdated}
                onDelete={handlePhoneNumberDeleted}
              />
            </Box>
          ) : (
            <Flex
              justify="center"
              align="center"
              h="100%"
              color={isDark ? "gray.400" : "gray.600"}
            >
              Select a phone number to view details
            </Flex>
          )}
        </Box>
      </Flex>

      <CreatePhoneNumberDialog
        isOpen={isCreateDialogOpen || isOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          onClose();
        }}
        onSuccess={handlePhoneNumberCreated}
      />

      <ImportPhoneNumberDialog
        isOpen={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        onSuccess={handlePhoneNumberCreated}
        agents={agents.map((agent) => ({
          id: agent.agent_id,
          name: agent.agent_name || agent.agent_id,
        }))}
      />

      <Modal isOpen={isNoAgentsOpen} onClose={onNoAgentsClose}>
        <ModalOverlay />
        <ModalContent bg={isDark ? "gray.800" : "white"}>
          <ModalBody pt={6}>
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              bg="transparent"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                No agents found
              </AlertTitle>
              <AlertDescription maxWidth="sm">
                Please create an agent first
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onNoAgentsClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Layout>
  );
};

export default PhoneNumbers;
